import React, { useEffect, useState } from 'react';
import { Activity, BadgeDollarSign, CircleSlash2, Database, Inbox, TrendingDown, Users } from 'lucide-react';
import { AppShell } from '../components/layout/AppShell';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { apiClient } from '../lib/api-client';
import { useToast } from '../hooks/useToast';

interface UsageResponse {
  period: { start: string; end: string };
  usage: {
    suppressed_attempts: number;
    bytes_avoided_estimate: number;
    distinct_installations: number;
    day_one_reduction_percent: number;
    current_reduction_percent: number;
    avoided_cost_estimate_usd: number | null;
  };
  sources: {
    transmission_v1: { records_received: number; bytes_received: number };
  };
  avoidance_estimate: {
    configured: boolean;
    cost_per_gib_usd: number | null;
    disclaimer: string;
  };
  pricing: { configured: boolean };
}

function formatBytes(value: number): string {
  if (!value) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  return `${(value / 1024 ** index).toFixed(index > 1 ? 2 : 0)} ${units[index]}`;
}

export const BillingPage: React.FC = () => {
  const [result, setResult] = useState<UsageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const { error: showError } = useToast();

  useEffect(() => {
    apiClient.getBillingUsage()
      .then((value) => setResult(value as UsageResponse))
      .catch((error: Error) => showError(error.message || 'Failed to load usage'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <AppShell><div className="h-72 animate-pulse rounded-2xl bg-muted" /></AppShell>;
  const usage = result?.usage;
  return (
    <AppShell>
      <div className="space-y-7">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div><p className="text-xs font-semibold uppercase tracking-[0.16em] text-indigo-600">Measured usage</p><h1 className="mt-2 text-3xl font-bold tracking-tight">Usage & transmission savings</h1><p className="mt-2 text-sm text-muted-foreground">Control-plane aggregates for the current UTC month. No placeholder currency estimates.</p></div>
          {result && <Badge variant="outline">{result.period.start} → {result.period.end}</Badge>}
        </header>

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <UsageCard icon={<Inbox />} label="Transmission records" value={Number(result?.sources.transmission_v1.records_received || 0).toLocaleString()} />
          <UsageCard icon={<Database />} label="Transmission bytes" value={formatBytes(Number(result?.sources.transmission_v1.bytes_received || 0))} />
          <UsageCard icon={<CircleSlash2 />} label="Suppressed attempts" value={Number(usage?.suppressed_attempts || 0).toLocaleString()} />
          <UsageCard icon={<Activity />} label="Estimated bytes avoided" value={formatBytes(Number(usage?.bytes_avoided_estimate || 0))} />
          <UsageCard icon={<Users />} label="Distinct installations" value={Number(usage?.distinct_installations || 0).toLocaleString()} />
          <UsageCard icon={<TrendingDown />} label="Current daily reduction" value={`${Number(usage?.current_reduction_percent || 0).toFixed(1)}%`} />
          <UsageCard icon={<BadgeDollarSign />} label="Estimated cost avoided" value={usage?.avoided_cost_estimate_usd === null || usage?.avoided_cost_estimate_usd === undefined ? 'Not configured' : formatUsd(usage.avoided_cost_estimate_usd)} />
        </section>

        <div className="grid gap-5 lg:grid-cols-2">
          <Card><CardHeader><h2 className="font-semibold">Reduction trend</h2></CardHeader><CardContent><div className="grid grid-cols-2 gap-3"><Metric label="Day one" value={`${Number(usage?.day_one_reduction_percent || 0).toFixed(1)}%`} /><Metric label="Current day" value={`${Number(usage?.current_reduction_percent || 0).toFixed(1)}%`} /></div><p className="mt-3 text-xs leading-5 text-muted-foreground">Reduction is SDK-counted suppressed attempts divided by transmitted plus suppressed attempts for each UTC day.</p></CardContent></Card>
          <Card className="border-amber-200 bg-amber-50/60 dark:border-amber-900 dark:bg-amber-950/20"><CardHeader><h2 className="font-semibold">{result?.avoidance_estimate.configured ? 'Avoided-cost estimate configured' : 'Avoided-cost estimate not configured'}</h2></CardHeader><CardContent><p className="text-sm leading-6 text-muted-foreground">{result?.avoidance_estimate.configured ? `Using your planning rate of ${formatUsd(Number(result.avoidance_estimate.cost_per_gib_usd))} per GiB. ` : 'Add an optional USD/GiB planning rate in Settings to estimate avoided cost. '}This is not LogLimit product pricing or an invoice.</p></CardContent></Card>
        </div>
      </div>
    </AppShell>
  );
};

function UsageCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return <Card><CardContent className="flex items-center gap-4 p-5"><div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700 [&>svg]:h-5 [&>svg]:w-5 dark:bg-indigo-950 dark:text-indigo-300">{icon}</div><div><p className="text-xl font-bold tabular-nums">{value}</p><p className="text-xs text-muted-foreground">{label}</p></div></CardContent></Card>;
}

function Metric({ label, value }: { label: string; value: string }) {
  return <div className="rounded-lg border p-4"><p className="text-2xl font-bold tabular-nums">{value}</p><p className="mt-1 text-xs text-muted-foreground">{label}</p></div>;
}

function formatUsd(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(value);
}
